import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../services/apiService';
import { EduVSearchResult, ExportConfiguration, ExportPushResult, SummaryRef } from '../types';
import { logger } from '../utils/logger';

interface EduVExportFlowProps {
    fileId: string;
    summaryRef: SummaryRef;
    /** Alle EDU_V-configuraties voor dit template */
    configs: ExportConfiguration[];
    /** Terug naar het gewone deelmenu */
    onBack: () => void;
    /** Hele deel-dialoog sluiten */
    onClose: () => void;
}

// zoeken → leerling kiezen (in de resultatenlijst) → bevestigen → link-call →
// resultaat. Alle stappen leven in dezelfde dialoog.
type FlowStep = 'search' | 'confirm' | 'linking' | 'success' | 'error';

// Het LAS kent geen zoekparameter op naam; de backend haalt de complete
// leerlingenlijst op en filtert die. Zonder ondergrens zou de hele
// schoolpopulatie in beeld komen.
const MIN_SEARCH_LENGTH = 2;

const EduVExportFlow: React.FC<EduVExportFlowProps> = ({
    fileId,
    summaryRef,
    configs,
    onBack,
    onClose,
}) => {
    const { t } = useTranslation();
    const apiService = useMemo(() => new ApiService(), []);

    const [step, setStep] = useState<FlowStep>('search');
    const [selectedConfigId, setSelectedConfigId] = useState<string>(configs[0]?.id ?? '');
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    // Leerlingnamen en ECK iD's uit het LAS: uitsluitend in deze
    // component-state houden; niet persisten, niet loggen, niet doorgeven aan
    // analytics.
    const [results, setResults] = useState<EduVSearchResult[]>([]);
    const [truncated, setTruncated] = useState(false);
    const [linkConfigId, setLinkConfigId] = useState<string>('');
    const [selectedResult, setSelectedResult] = useState<EduVSearchResult | null>(null);
    const [linkResult, setLinkResult] = useState<ExportPushResult | null>(null);

    const selectedConfig = configs.find((config) => config.id === selectedConfigId) ?? configs[0];
    // Zonder expliciete methode is PROGRESS de veilige aanname: dat scherm
    // belooft niet dat er inhoud verstuurd wordt.
    const isWebhook = selectedConfig?.exportMethod === 'LAS_WEBHOOK';
    const trimmedTerm = searchTerm.trim();
    const termTooShort = trimmedTerm.length < MIN_SEARCH_LENGTH;

    // Resultaten horen bij één configuratie; bij wisselen opnieuw zoeken.
    const changeConfig = (configId: string) => {
        setSelectedConfigId(configId);
        setResults([]);
        setTruncated(false);
        setHasSearched(false);
        setSearchError(null);
    };

    const search = async () => {
        if (termTooShort || isSearching) return;
        setIsSearching(true);
        setSearchError(null);
        try {
            const response = await apiService.searchEduV(
                fileId,
                summaryRef,
                selectedConfigId,
                trimmedTerm
            );
            setResults(response.results);
            setTruncated(response.truncated);
            setLinkConfigId(response.configId);
            setHasSearched(true);
        } catch (error: any) {
            // Alleen de foutstatus loggen; response-data kan leerlinggegevens bevatten.
            logger.error('Edu-V search failed with status:', error?.response?.status);
            const message = error?.response?.data?.error;
            setSearchError(message ? message.toString() : t('shareDialog.eduv.error'));
        } finally {
            setIsSearching(false);
        }
    };

    const link = async () => {
        if (!selectedResult) return;
        setStep('linking');
        try {
            const result = await apiService.linkEduV(
                fileId,
                summaryRef,
                linkConfigId || selectedConfigId,
                // Het ECK iD letterlijk doorgeven: niet samenstellen, niet parsen.
                selectedResult.id
            );
            setLinkResult(result);
            setStep(result.success ? 'success' : 'error');
        } catch (error: any) {
            logger.error('Edu-V link failed with status:', error?.response?.status);
            const message = error?.response?.data?.error;
            setLinkResult({
                success: false,
                message: message ? message.toString() : t('shareDialog.eduv.error'),
            });
            setStep('error');
        }
    };

    // Twee backend-fouten zijn geen gebruikersfout; die verdienen een
    // handelingsperspectief onder de letterlijke melding.
    const extraHint = (message?: string): string | null => {
        const lower = (message ?? '').toLowerCase();
        if (lower.includes('gegevensdienst')) return t('shareDialog.eduv.consentHint');
        if (lower.includes('authenticatie')) return t('shareDialog.eduv.adminHint');
        return null;
    };

    const inputClass =
        'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500';
    const primaryButtonClass =
        'px-4 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
    const secondaryButtonClass =
        'px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors';

    if (step === 'search') {
        const searchHint = extraHint(searchError ?? undefined);
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('shareDialog.eduv.searchTitle')}
                </h3>

                {configs.length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.eduv.configLabel')}
                        </label>
                        <select
                            value={selectedConfigId}
                            onChange={(e) => changeConfig(e.target.value)}
                            disabled={isSearching}
                            className={inputClass}
                        >
                            {configs.map((config) => (
                                <option key={config.id} value={config.id}>{config.name}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('shareDialog.eduv.searchLabel')}
                    </label>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        // Zoeken op knopdruk (of Enter): elke zoekopdracht haalt
                        // de complete leerlingenlijst van de school op, dus geen
                        // type-ahead per toetsaanslag.
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                search();
                            }
                        }}
                        disabled={isSearching}
                        autoFocus
                        className={inputClass}
                    />
                </div>

                {trimmedTerm.length > 0 && termTooShort && (
                    <p className="text-xs text-red-600">{t('shareDialog.eduv.searchMinChars')}</p>
                )}

                <div className="flex justify-end space-x-2">
                    <button onClick={onBack} disabled={isSearching} className={secondaryButtonClass}>
                        {t('shareDialog.eduv.back')}
                    </button>
                    <button
                        onClick={search}
                        disabled={termTooShort || isSearching}
                        className={primaryButtonClass}
                    >
                        {t('shareDialog.eduv.search')}
                    </button>
                </div>

                {isSearching && (
                    <div className="flex justify-center py-4">
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin"></div>
                    </div>
                )}

                {searchError && !isSearching && (
                    <div className="text-center space-y-2">
                        <p className="text-sm text-red-600">{searchError}</p>
                        {searchHint && <p className="text-xs text-gray-600">{searchHint}</p>}
                        <button onClick={search} className={secondaryButtonClass}>
                            {t('shareDialog.eduv.retry')}
                        </button>
                    </div>
                )}

                {hasSearched && !isSearching && !searchError && results.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">
                        {t('shareDialog.eduv.empty')}
                    </p>
                )}

                {hasSearched && !isSearching && !searchError && results.length > 0 && (
                    <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">
                            {t('shareDialog.eduv.resultsLabel')}
                        </div>
                        {truncated && (
                            <div className="mb-2 px-3 py-2 text-xs rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                                {t('shareDialog.eduv.truncated')}
                            </div>
                        )}
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {/* Index als key, niet het id: het ECK iD hoort
                                nergens buiten de state en de link-request. */}
                            {results.map((result, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        setSelectedResult(result);
                                        setStep('confirm');
                                    }}
                                    className="w-full flex items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900 text-sm truncate">{result.name}</div>
                                        {/* Het LAS gebruikt alias juist om naamgenoten
                                            te onderscheiden; altijd tonen als hij er is. */}
                                        {result.alias && (
                                            <div className="text-gray-500 text-xs truncate">{result.alias}</div>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    if (step === 'confirm' && selectedResult) {
        return (
            <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">
                    {t('shareDialog.eduv.searchTitle')}
                </h3>

                {/* De gebruiker moet zien wát er verstuurd wordt: bij PROGRESS
                    blijft de verslaginhoud in Notizy, bij LAS_WEBHOOK niet. */}
                {isWebhook ? (
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <svg className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <p className="text-sm font-medium text-amber-900">
                                {t('shareDialog.eduv.confirmWebhook')}
                            </p>
                            <p className="text-xs text-amber-800 mt-1">
                                {t('shareDialog.eduv.confirmWebhookBody', {
                                    name: selectedResult.name,
                                    system: selectedConfig?.name ?? '',
                                })}
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                        <p className="text-sm font-medium text-gray-900">
                            {t('shareDialog.eduv.confirmProgress')}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                            {t('shareDialog.eduv.confirmProgressBody', { name: selectedResult.name })}
                        </p>
                    </div>
                )}

                <div className="flex justify-end space-x-2">
                    <button onClick={() => setStep('search')} className={secondaryButtonClass}>
                        {t('shareDialog.eduv.back')}
                    </button>
                    <button onClick={link} className={primaryButtonClass}>
                        {isWebhook
                            ? t('shareDialog.eduv.actionSend')
                            : t('shareDialog.eduv.actionLink')}
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'linking') {
        return (
            <div className="flex flex-col items-center py-8 text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin mb-4"></div>
                <div className="text-sm font-medium text-gray-900">{t('shareDialog.eduv.busy')}</div>
                <div className="text-xs text-gray-500 mt-1">{t('shareDialog.eduv.busyHint')}</div>
            </div>
        );
    }

    if (step === 'success') {
        return (
            <div className="flex flex-col items-center py-6 text-center">
                <svg className="w-8 h-8 text-primary-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-gray-900">
                    {linkResult?.message ||
                        (isWebhook
                            ? t('shareDialog.eduv.successWebhook')
                            : t('shareDialog.eduv.successProgress'))}
                </p>
                <button onClick={onClose} className={`${secondaryButtonClass} mt-4`}>
                    {t('shareDialog.eduv.close')}
                </button>
            </div>
        );
    }

    // step === 'error' (of confirm zonder selectie, wat niet voor hoort te komen)
    const linkHint = extraHint(linkResult?.message);
    return (
        <div className="flex flex-col items-center py-6 text-center">
            <svg className="w-8 h-8 text-red-600 mb-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-gray-900">
                {linkResult?.message || t('shareDialog.eduv.error')}
            </p>
            {linkHint && <p className="text-xs text-gray-600 mt-2 max-w-xs">{linkHint}</p>}
            <div className="flex space-x-2 mt-4">
                {/* Geen automatische retries: bij LAS_WEBHOOK is opnieuw versturen
                    een gewone POST en kan dubbel landen. */}
                <button onClick={link} className={primaryButtonClass}>
                    {t('shareDialog.eduv.retry')}
                </button>
                <button onClick={() => setStep('search')} className={secondaryButtonClass}>
                    {t('shareDialog.eduv.back')}
                </button>
            </div>
        </div>
    );
};

export default EduVExportFlow;
